/* @flow */
/* eslint-disable no-new */

/**
 * Bootstrap app.
 */
import Vue from 'vue' // runtime-only
import Vuelidate from 'vuelidate'
import { Toasty } from 'components/UI/Toasty'
import http from 'axios'
import Shortkey from 'vue-shortkey'
import * as Rx from 'rxjs'
import * as VueRx from 'vue-rx'
import VCalendar from 'v-calendar'
import 'v-calendar/lib/v-calendar.min.css'
import VueVisible from 'vue-visible'
import vueHeadful from 'vue-headful'
import { LastEdits } from 'components/Home'
import Flows from 'components/Applications/Flows/Flows'
import { Auth } from 'services'
import { abilitiesPlugin } from '@casl/vue'
import { i18n, SetupLocales } from 'i18n'
import PortalVue from 'portal-vue'
import 'animate.css'
/**
 * Setup Vue plugins.
 */
Vue.use(Toasty)
Vue.use(Shortkey, { prevent: ['.no-short-keys'] })
Vue.use(Rx)
Vue.use(VueRx)
Vue.use(Vuelidate)
Vue.use(VCalendar, {
  firstDayOfWeek: 2
})
Vue.use(VueVisible)
Vue.use(abilitiesPlugin)
Vue.use(PortalVue)

/** Global components **/
Vue.component('vue-headful', vueHeadful)
Vue.component('last-edits', LastEdits)
Vue.component('flows', Flows)

// Filter for document.title formatting
Vue.filter('formatTitle', value => {
  if (!value) return i18n.t('PRODUCT_NAME') || 'Cuesta'
  value = capitalize(value)
  // Intersperse characters with zero width white space to prevent accidental manatee title matching
  value = value.split('').join('​') // WARNING: invisible char lurks in the join argument!
  // Note: Make sure the title continues to contain 'Cuesta' as steller uses this to identify the tab
  return (i18n.t('PRODUCT_NAME') || 'Cuesta') + ' — ' + value
})

function capitalize(value) {
  if (!value) return ''
  value = value.toString()
  return value.charAt(0).toUpperCase() + value.slice(1)
}

// Capitalize a string
Vue.filter('capitalize', capitalize)

// To focus input elements etc
Vue.directive('focus', {
  inserted: function(el) {
    el.focus()
  }
})

/**
 * Custom key codes aliases
 */
Vue.config.keyCodes = {
  r: 82
}

/**
 * Ignored elements
 */
Vue.config.ignoredElements = [
  'xml',
  'block',
  'sep',
  'value',
  'field',
  'mutation',
  'shadow',
  'category',
  'statement' // blockly
]
import router from 'router'

/**
 * Intercept router change and replace escaped '/' character.
 * Hackish way to tackle monaco escaping '/' character in the fragment uri part
 * Until the issue in monaco repo gets fixed https://github.com/Microsoft/monaco-editor/issues/1375
 */
const ESCAPED_SLASH = '%2F'
router.beforeEach((to, from, next) => {
  if (to.path.includes(ESCAPED_SLASH)) {
    next({ path: decodeURIComponent(to.path).replace('//', '/') })
  } else {
    next()
  }
})

/**
 * Intercept 401 requests and redirect.
 */
http.interceptors.response.use(
  response => response,
  error => {
    // Allows requestors to bypass the automatic logout functionality
    if (error.config && error.config.bypassLogoutInterceptor) return Promise.reject(error)

    const currentRoute = router.currentRoute.name
    if (!error.response && currentRoute !== 'login') {
      Auth.logOut({ params: { error: 'COMM_ERROR' } })
    }
    if (error.response && error.response.status === 401 && currentRoute !== 'login') {
      Auth.logOut({ query: { status: 401 } })
    }

    return Promise.reject(error)
  }
)

/**
 * While we're on the login page we dont tolerate any requests besides those to the authenticate, about or 2fa endpoints.
 */
http.interceptors.request.use(request => {
  if (
    router.currentRoute.name !== 'login' ||
    request.url.indexOf('authenticate') >= 0 ||
    request.url.indexOf('about') >= 0 ||
    request.url.indexOf('2fa') >= 0
  ) {
    return request
  }
  throw new http.Cancel('Cancelling unauthenticated request')
})

/**
 * Register a protocol-handler for hub urls
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/registerProtocolHandler/Web-based_protocol_handlers
 */
if (window.navigator.registerProtocolHandler) {
  window.navigator.registerProtocolHandler(
    'web+hubinstall',
    `${window.location.protocol}//${window.location.host}/#/%s`,
    'Hub install'
  )
}

/**
 * Start app.
 */
import App from './App'

Vue.mixin({
  data() {
    return {
      get keyCodes() {
        return { enter: 13, esc: 27, arrowUp: 40, arrowDown: 38 }
      }
    }
  }
})

import { store } from './store'

new Vue({
  render: h => h(App),
  router: router,
  store: store,
  i18n
}).$mount('[app]')

// Expose the router
Vue.router = router

/**
 * Setup i18n.
 */
SetupLocales()

/**
 * Setup main stylesheet + libs.\
 */
/* eslint-disable no-unused-vars */
import * as SUI from 'fomantic-ui-less/semantic.less'
import './scss/app.scss'
